"use client";

import { c as _c } from "react/compiler-runtime";
import { focusRingClassName } from "@/components/ui/control-surface";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import cn from "twsx";

/** Use when switching between views of the same page. */
const Tabs = TabsPrimitive.Root;
type MutableRefObject<T> = {
  current: T;
};

// Width (px) of the edge fade and the breathing room kept when scrolling the
// active tab into view.
const TABS_SCROLL_FADE = 24;
type TabsScrollEdges = {
  start: boolean;
  end: boolean;
};

/**
 * Drives the horizontal-scroll affordance for {@link TabsList}: tracks which
 * edges have hidden content (for the fade mask) and keeps the active tab in
 * view. When the strip doesn't overflow, both edges report `false` and no mask
 * is applied, so a non-scrolling list looks untouched. Scrolling is constrained
 * to the X axis so selecting a tab never nudges the page vertically.
 */
function useTabsScrollAffordance() {
  const $ = _c(6);
  const elementRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      start: false,
      end: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [edges, setEdges] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = elementRef.current;
      if (!el) {
        return;
      }
      const maxScroll = el.scrollWidth - el.clientWidth;
      const nextEdges = {
        start: el.scrollLeft > 1,
        end: el.scrollLeft < maxScroll - 1
      };
      setEdges(currentEdges => currentEdges.start === nextEdges.start && currentEdges.end === nextEdges.end ? currentEdges : nextEdges);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const syncEdges = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el_0 = elementRef.current;
      if (!el_0) {
        return;
      }
      const revealActiveTab = () => {
        const active = el_0.querySelector("[data-state=\"active\"]");
        if (active) {
          const elRect = el_0.getBoundingClientRect();
          const activeRect = active.getBoundingClientRect();
          if (activeRect.left < elRect.left) {
            el_0.scrollLeft = el_0.scrollLeft - (elRect.left - activeRect.left + TABS_SCROLL_FADE);
          } else {
            if (activeRect.right > elRect.right) {
              el_0.scrollLeft = el_0.scrollLeft + (activeRect.right - elRect.right + TABS_SCROLL_FADE);
            }
          }
        }
        syncEdges();
      };
      revealActiveTab();
      el_0.addEventListener("scroll", syncEdges, {
        passive: true
      });
      const resizeObserver = new ResizeObserver(syncEdges);
      resizeObserver.observe(el_0);
      const stateObserver = new MutationObserver(() => {
        el_0.querySelectorAll("[data-selection-pending=\"true\"]").forEach(_temp);
        revealActiveTab();
      });
      stateObserver.observe(el_0, {
        attributes: true,
        attributeFilter: ["data-state"],
        subtree: true
      });
      return () => {
        el_0.removeEventListener("scroll", syncEdges);
        resizeObserver.disconnect();
        stateObserver.disconnect();
      };
    };
    t3 = [syncEdges];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== edges) {
    t4 = {
      elementRef,
      edges
    };
    $[4] = edges;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

// The edge fade masks whichever side has hidden tabs (TABS_SCROLL_FADE wide).
function _temp(trigger) {
  return trigger.removeAttribute("data-selection-pending");
}
function scrollEdge(edges: TabsScrollEdges) {
  if (edges.start && edges.end) return "both";
  if (edges.end) return "end";
  if (edges.start) return "start";
  return undefined;
}
const scrollMaskClassName = ["data-[scroll-edge=both]:[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]", "data-[scroll-edge=end]:[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]", "data-[scroll-edge=start]:[mask-image:linear-gradient(to_right,transparent,black_24px)]"];

// "view": Linear's raised view-tab pills (28px, bg-control with a .105 ring;
// selected fills fill-tab-selected). "quiet": transparent pills that only raise
// when selected, used inside the command menu. `layout="bar"` makes the list a
// 40px strip between dividers, as under the ⌘K input.
type TabsVariant = "view" | "quiet";
const TabsVariantContext = createContext<TabsVariant>("view");
const TabsList = t0 => {
  const $ = _c(32);
  let className;
  let props;
  let ref;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      variant: t1,
      layout: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const variant = t1 === undefined ? "view" : t1;
  const layout = t2 === undefined ? "inline" : t2;
  const {
    elementRef,
    edges
  } = useTabsScrollAffordance();
  let t3;
  if ($[6] !== elementRef || $[7] !== ref) {
    t3 = node => {
      elementRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref && typeof ref === "object") {
          (ref as MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }
    };
    $[6] = elementRef;
    $[7] = ref;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const setRefs = t3;
  let t4;
  if ($[9] !== edges) {
    t4 = scrollEdge(edges);
    $[9] = edges;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = variant === "quiet" ? "gap-0.5" : "gap-1.5";
  const t6 = layout === "bar" && "h-10 shrink-0 border-y border-border-subtle px-3.5";
  let t7;
  if ($[11] !== className || $[12] !== t5 || $[13] !== t6) {
    t7 = cn("group/tabs-list relative flex h-auto w-full max-w-full flex-row items-center overflow-x-auto bg-transparent p-1", "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", scrollMaskClassName, t5, t6, className);
    $[11] = className;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== props || $[16] !== setRefs || $[17] !== t4 || $[18] !== t7 || $[19] !== variant) {
    t8 = <TabsPrimitive.List ref={setRefs} data-variant={variant} data-scroll-edge={t4} className={t7} {...props} />;
    $[15] = props;
    $[16] = setRefs;
    $[17] = t4;
    $[18] = t7;
    $[19] = variant;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8 || $[22] !== variant) {
    t9 = <TabsVariantContext.Provider value={variant}>{t8}</TabsVariantContext.Provider>;
    $[21] = t8;
    $[22] = variant;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== edges.start) {
    t10 = edges.start && <div aria-hidden="true" className={cn("pointer-events-none absolute inset-y-0 left-0 z-10 flex w-7 items-center justify-start text-text-tertiary", "bg-gradient-to-r from-bg-surface via-bg-surface/90 to-transparent")} data-tabs-scroll-affordance="start"><ChevronLeft className="size-3.5" strokeWidth={1.75} /></div>;
    $[24] = edges.start;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== edges.end) {
    t11 = edges.end && <div aria-hidden="true" className={cn("pointer-events-none absolute inset-y-0 right-0 z-10 flex w-7 items-center justify-end text-text-tertiary", "bg-gradient-to-l from-bg-surface via-bg-surface/90 to-transparent")} data-tabs-scroll-affordance="end"><ChevronRight className="size-3.5" strokeWidth={1.75} /></div>;
    $[26] = edges.end;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t9) {
    t12 = <div className="relative -m-1 min-w-0 max-w-bleed-1" data-tabs-scroll-container={true}>{t9}{t10}{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
TabsList.displayName = TabsPrimitive.List.displayName;

// A tab reads as selected when it is active, or while a click on it is still
// resolving (selection-pending / navigation-pending); the previously active tab
// releases its selected look as soon as another tab is pending.
const tabsTriggerVariants = {
  view: ["h-7 bg-bg-control shadow-tab-selected hover:bg-fill-tab-hover", "hover:[box-shadow:lch(0_0_0/0.15)_0px_0px_0px_1px,var(--elevation-low)]", "data-[state=active]:bg-fill-tab-selected data-[state=active]:[box-shadow:lch(0_0_0/0.105)_0px_0px_0px_1px]", "data-[selection-pending=true]:bg-fill-tab-selected has-[[data-navigation-pending=true]]:bg-fill-tab-selected", "group-has-[[data-selection-pending=true]]/tabs-list:data-[state=active]:!bg-bg-control", "group-has-[[data-navigation-pending=true]]/tabs-list:data-[state=active]:!bg-bg-control"],
  quiet: ["h-6 px-2 hover:bg-fill-tab-hover", "data-[state=active]:bg-bg-control data-[state=active]:shadow-tab-selected", "data-[selection-pending=true]:bg-bg-control data-[selection-pending=true]:shadow-tab-selected", "group-has-[[data-selection-pending=true]]/tabs-list:data-[state=active]:!bg-transparent", "group-has-[[data-selection-pending=true]]/tabs-list:data-[state=active]:!shadow-none", "group-has-[[data-navigation-pending=true]]/tabs-list:data-[state=active]:!bg-transparent", "group-has-[[data-navigation-pending=true]]/tabs-list:data-[state=active]:!shadow-none"]
};
const TabsTrigger = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  const variant = useContext(TabsVariantContext);
  const t1 = tabsTriggerVariants[variant];
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("group/tab relative flex shrink-0 cursor-pointer select-none items-center gap-1.5 whitespace-nowrap", "rounded-full border border-transparent px-2.5 text-sm font-medium text-text-tertiary outline-none", "transition-control duration-150 hover:duration-0 hover:text-text-secondary disabled:opacity-disabled [&_svg]:size-3.5", "focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent", "data-[state=active]:text-text-primary data-[selection-pending=true]:text-text-primary", "has-[[data-navigation-pending=true]]:text-text-primary", "group-has-[[data-selection-pending=true]]/tabs-list:data-[state=active]:!text-text-tertiary", "group-has-[[data-navigation-pending=true]]/tabs-list:data-[state=active]:!text-text-tertiary", "data-[selection-pending=true]:transition-none has-[[data-navigation-pending=true]]:transition-none", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <TabsPrimitive.Trigger ref={ref} className={t2} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
const TabsContent = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      layout: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
  }
  const layout = t1 === undefined ? "block" : t1;
  const t2 = layout === "contents" && "outline-none data-[state=active]:contents";
  let t3;
  if ($[5] !== className || $[6] !== t2) {
    t3 = cn(focusRingClassName, t2, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== props || $[9] !== ref || $[10] !== t3) {
    t4 = <TabsPrimitive.Content ref={ref} className={t3} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
TabsContent.displayName = TabsPrimitive.Content.displayName;

// Count beside a tab label ("Students 1,666"): plain tabular 12/450 text that
// brightens with its tab, as Linear renders view counts.
const TabsCountBadge = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sm font-book tabular-nums text-text-quaternary", "group-hover/tab:text-text-tertiary group-data-[state=active]/tab:text-text-tertiary", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
TabsCountBadge.displayName = "TabsCountBadge";
export { Tabs, TabsContent, TabsCountBadge, TabsList, TabsTrigger };